import { toast as sonnerToast } from 'sonner'
import type { InsufficientCreditsError } from '@/features/news/types/ai.types'

/**
 * Toast Notification Utility
 *
 * Wrapper around Sonner toast library for consistent notifications.
 * Used primarily for API error handling and user feedback.
 */

export const toast = {
  /**
   * Display success message
   */
  success(message: string, description?: string) {
    sonnerToast.success(message, {
      description,
      duration: 3000,
    })
  },

  /**
   * Display error message
   */
  error(message: string, description?: string) {
    sonnerToast.error(message, {
      description,
      duration: 5000,
    })
  },

  /**
   * Display warning message
   */
  warning(message: string, description?: string) {
    sonnerToast.warning(message, {
      description,
      duration: 4000,
    })
  },

  /**
   * Display info message
   */
  info(message: string, description?: string) {
    sonnerToast.info(message, {
      description,
      duration: 3000,
    })
  },

  /**
   * Display credit exhaustion error with specific formatting
   */
  creditExhausted(error: InsufficientCreditsError) {
    sonnerToast.error('크레딧이 부족합니다', {
      description: `${error.message}\n자정(UTC)에 ${error.daily_limit}개의 크레딧이 충전됩니다.`,
      duration: 6000,
    })
  },

  /**
   * Display rate limit error
   */
  rateLimit(message?: string) {
    sonnerToast.warning('요청 제한 초과', {
      description: message || '잠시 후 다시 시도해주세요.',
      duration: 5000,
    })
  },

  /**
   * Display authentication error
   */
  authError(message?: string) {
    sonnerToast.error('인증 오류', {
      description: message || '로그인이 필요합니다.',
      duration: 4000,
    })
  },

  /**
   * Display server error
   */
  serverError(message?: string) {
    sonnerToast.error('서버 오류', {
      description: message || '문제가 지속되면 고객 지원팀에 문의해주세요.',
      duration: 5000,
    })
  },

  /**
   * Display network error
   */
  networkError(message?: string) {
    sonnerToast.error('네트워크 오류', {
      description: message || '인터넷 연결을 확인해주세요.',
      duration: 4000,
    })
  },
}
